import { Card } from "@/components/ui/card";
import { MultipleChoices } from "@/pages/PredictionMarket/MyEntry";
import { ClockIcon, ThumbaCoin } from "@roman/shared/components/Icons";
import type { PredictionMarketMyResult } from "@roman/shared/types/PredictionMarket";
import { cn } from "@roman/shared/utils";
import { formatNumberWithCommas } from "@roman/shared/utils/formatNumberWithCommas";
import { PredictionMarketType } from "@roman/shared/utils/predictionMarket/utils";
import { format } from "date-fns";
import { ChevronRightIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { CorrectCircleIcon } from "../CorrectCircleIcon";
import { IncorrectCircleIcon } from "../IncorrectCircleIcon";
import { Title } from "../MarketCardList/Title";
import { Tag } from "../Tag";

export const MyEntryResultCard: React.FC<{
  predictionMarketMyResult: PredictionMarketMyResult;
}> = ({ predictionMarketMyResult }) => {
  const marketType = predictionMarketMyResult.type;
  const choices = predictionMarketMyResult.choices;

  let color = "";
  if (marketType === PredictionMarketType.TwoChoice) {
    color = choices[0].id === predictionMarketMyResult.myChoice.id ? "text-teal" : "text-orange";
  }
  const percentage =
    predictionMarketMyResult.myChoice.probability * 100 >= 1
      ? Math.round(predictionMarketMyResult.myChoice.probability * 100) === 100
        ? "99"
        : Math.round(predictionMarketMyResult.myChoice.probability * 100)
      : "<1";

  return (
    <Card className="border-none shadow-md">
      <div className="cursor-pointer overflow-hidden w-full rounded-xl">
        <Link to={`/prediction-market/details/${predictionMarketMyResult.predictionMarketId}`}>
          <div className="px-[15px] pt-2.5 pb-5">
            <div className="flex items-center justify-between gap-2">
              <Tag text={predictionMarketMyResult.label} />
              <div className="flex items-center justify-center gap-1">
                <ClockIcon className="h-4 w-4 text-gray" />
                <div className="text-xs text-gray font-medium">
                  {format(predictionMarketMyResult.entryCloseAt, "yyyy年M月d日 HH:mm")} 終了
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-[10px] mt-[7px]">
              {predictionMarketMyResult.primaryImageURL && (
                <img
                  src={predictionMarketMyResult.primaryImageURL}
                  alt={predictionMarketMyResult.title}
                  className="object-cover rounded-lg h-[46px] w-[46px]"
                />
              )}
              <Title
                text={predictionMarketMyResult.title}
                className="text-black text-lg font-bold cursor-pointer text-ellipsis overflow-hidden"
              />
            </div>
            <div className="flex items-center gap-2 justify-between relative">
              {marketType === PredictionMarketType.TwoChoice ? (
                <div className="text-lg flex items-center gap-1">
                  <div>
                    {predictionMarketMyResult.prize ? (
                      <CorrectCircleIcon className={color} />
                    ) : (
                      <IncorrectCircleIcon className={color} />
                    )}
                  </div>
                  <span className="font-medium uppercase">{predictionMarketMyResult.myChoice.name}</span>
                  <div className="font-extrabold">{percentage}%</div>
                </div>
              ) : (
                <MultipleChoices myChoice={predictionMarketMyResult.myChoice} className="w-[90%]" />
              )}

              <div
                className={cn(
                  "w-[67px] h-[67px] absolute right-0  -translate-y-2/3",
                  marketType === PredictionMarketType.TwoChoice ? "top-1/2" : "",
                )}
              >
                {predictionMarketMyResult.prize ? (
                  <img
                    src="/assets/prediction-market/correct-stamp-mark.png"
                    alt="Correct Stamp Mark"
                    className="w-full h-full rotate-[-15deg]"
                  />
                ) : (
                  <img
                    src="/assets/prediction-market/incorrect-stamp-mark.png"
                    alt="Incorrect Stamp Mark"
                    className="w-full h-full rotate-[-15deg]"
                  />
                )}
              </div>
            </div>

            <div className="border border-[#91E9FF] rounded-md overflow-hidden flex divide-x divide-[#91E9FF]">
              <div className="w-1/2 flex flex-col items-center">
                <div className="py-1.5 text-center bg-secondary/10 text-[10px] text-dark-gray w-full font-medium">
                  使ったポイント
                </div>
                <div className="flex-1 flex text-black font-bold text-center items-center py-1.5">
                  {formatNumberWithCommas(predictionMarketMyResult.freePoints.toString())} pt
                </div>
              </div>

              <div className="w-1/2 flex flex-col items-center">
                <div className="py-1.5 text-center bg-secondary/10 text-[10px] text-dark-gray w-full font-medium">
                  獲得pt
                </div>
                <div className="flex-1 flex font-bold text-center items-center py-1.5 text-orange">
                  {predictionMarketMyResult.prize ? (
                    <span>+{formatNumberWithCommas(predictionMarketMyResult.prize.toString())} pt</span>
                  ) : (
                    "-"
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 pt-3">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-1 text-black font-bold text-sm">
                  <ThumbaCoin className="w-3 h-3" />
                  {formatNumberWithCommas(predictionMarketMyResult.freePointsPool.toString())} pt
                </div>
              </div>

              <div className="flex items-center text-primary text-xs font-bold hover:underline text-right">
                結果を見る <ChevronRightIcon className="inline h-4 w-4" strokeWidth="3" />
              </div>
            </div>
          </div>
        </Link>
      </div>
    </Card>
  );
};
